import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { MessageService } from 'primeng/api';

import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    PasswordModule,
    ButtonModule
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private messageService = inject(MessageService);

  loginForm = this.fb.group({
    password: ['', [Validators.required]]
  });

  isLoading = signal(false);
  returnUrl = signal<string | null>(null);
  host = signal<string | null>(null);
  loginError = signal<string | null>(null);

  ngOnInit() {
    // Get returnUrl and host from query params
    this.route.queryParams.subscribe(params => {
      this.returnUrl.set(params['returnUrl']);
      // Attempt to extract host from returnUrl if not explicitly provided
      if (this.returnUrl()) {
        try {
          const url = new URL(this.returnUrl()!);
          this.host.set(url.hostname);
        } catch (e) {
          // ignore
        }
      }
    });
  }

  onSubmit() {
    if (this.loginForm.invalid) return;

    this.isLoading.set(true);
    this.loginError.set(null);
    const { password } = this.loginForm.value;

    // If host is not provided via returnUrl, fall back to current browser hostname
    const effectiveHost = this.host() || window.location.hostname;

    this.authService.login(password!, effectiveHost).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        const token = response?.token;

        // Redirect back to original site with token for first-party cookie minting
        if (this.returnUrl()) {
          const redirectUrl = this.buildReturnUrlWithToken(this.returnUrl()!, token);
          window.location.href = redirectUrl;
          return;
        }

        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Authenticated successfully.' });
      },
      error: (err) => {
        this.isLoading.set(false);
        const detail = err?.error?.message || 'Invalid password.';
        this.loginError.set(detail);
        this.messageService.add({ severity: 'error', summary: 'Error', detail });
      }
    });
  }

  private buildReturnUrlWithToken(url: string, token?: string): string {
    if (!token) return url;

    try {
      const parsed = new URL(url);
      parsed.searchParams.set('token', token);
      return parsed.toString();
    } catch {
      // Fallback for non-standard URLs
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}token=${encodeURIComponent(token)}`;
    }
  }
}
